import { csrfFetch } from "../utils/csrfFetch";

// Action Types
const LOAD_USERS = "users/LOAD_USERS";
const UPDATE_USER = "users/UPDATE_USER";
const DELETE_USER = "users/DELETE_USER";
const REMOVE_USER = 'users/REMOVE_USER';
const DEACTIVATE_USER = 'users/DEACTIVATE_USER';

// Action Creators
export const loadUsers = (users) => ({
  type: LOAD_USERS,
  payload: users,
});

const updateUser = (user) => ({
  type: UPDATE_USER,
  payload: user,
});

const deactivateUser = (user) => ({
  type: DEACTIVATE_USER,
  payload: user,
});

const removeUser = (userId) => ({
  type: REMOVE_USER,
  userId
});

// Thunks
export const fetchUsersAdmin = () => async (dispatch) => {
  try {
    const response = await csrfFetch("/api/users");
    if (response.ok) {
      const data = await response.json();
      console.log("Fetched users data:", data);
      dispatch(loadUsers(data));
    } else {
      console.error("Error fetching users:", response.statusText);
    }
  } catch (error) {
    console.error("Error in fetchUsersAdmin:", error);
  }
};

export const fetchUsersUser = (accountId) => async (dispatch) => {
  try {
    const response = await csrfFetch('/api/users/');

    if (response.ok) {
      const users = await response.json();
      dispatch(loadUsers(users));
      return users;
    } else {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch users');
    }
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const updateUserAdmin = (userId, changes) => async (dispatch) => {
  try {
    const response = await csrfFetch(`/api/users/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(changes)
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error updating user:', errorData);
      throw new Error(errorData.errors?.[0] || errorData.error || 'Failed to update user');
    }

    const updatedUser = await response.json();
    dispatch(updateUser(updatedUser));
    return updatedUser;
  } catch (error) {
    console.error('Error updating user:', error);
    // Re-throw the error with a more specific message if possible
    throw new Error(error.message || 'An error occurred while updating the user. Please try again.');
  }
};

export const deleteUserAdmin = (userId) => async (dispatch) => {
  try {
    const response = await csrfFetch(`/api/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const updatedUser = await response.json();
      dispatch(deactivateUser(updatedUser.user));
      return null;
    } else {
      const errorData = await response.json().catch(() => null);
      console.error('Deactivate user error response:', errorData);

      if (errorData && errorData.errors) {
        return errorData.errors;
      } else {
        return [`Server error: ${response.status} ${response.statusText}`];
      }
    }
  } catch (error) {
    console.error('Exception during user deactivation:', error);
    return [`An error occurred: ${error.toString()}`];
  }
};

export const reactivateUser = (userId) => async (dispatch) => {
  try {
    const response = await csrfFetch(`/api/users/${userId}/reactivate`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const updatedUser = await response.json();
      dispatch(updateUser(updatedUser.user));
      return null;
    } else {
      const errorData = await response.json().catch(() => null);
      if (errorData && errorData.errors) {
        return errorData.errors;
      } else {
        return [`Server error: ${response.status} ${response.statusText}`];
      }
    }
  } catch (error) {
    console.error('Exception during user reactivation:', error);
    return [`An error occurred: ${error.toString()}`];
  }
};

export const getUserById = (id) => async (dispatch) => {
  try {
    const response = await csrfFetch(`/api/users/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch user');
    }
    const user = await response.json();
    dispatch(updateUser(user));
    return user;
  } catch (error) {
    console.error('Error fetching user:', error);
    throw error;
  }
};

// Reducer
const initialState = [];

export default function usersReducer(state = initialState, action) {
  switch (action.type) {
    case LOAD_USERS:
      // console.log("Loading users into state:", action.payload);
      return action.payload;
    case UPDATE_USER:
      return state.map(user =>
        user.id === action.payload.id ? action.payload : user
      );
    case DELETE_USER:
      return state.filter(user => user.id !== action.payload);
    case REMOVE_USER:
      return state.filter(user => user.id !== action.userId);
    case DEACTIVATE_USER:
      return state.map(user =>
        user.id === action.payload.id ? action.payload : user
      );
    default:
      return state;
  }
}
